import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { api } from '../lib/api';
import { X, HelpCircle } from 'lucide-react';
import toast from 'react-hot-toast';

interface HelperModalProps {
  helperKey: string;
  title: string;
  content: string | React.ReactNode;
  showOnce?: boolean;
  showPermanentOption?: boolean;
}

export default function HelperModal({
  helperKey,
  title,
  content,
  showOnce = true,
  showPermanentOption = true
}: HelperModalProps) {
  const [isOpen, setIsOpen] = useState(false);
  const queryClient = useQueryClient();

  // Fetch helper status
  const { data: helperStatus, isLoading } = useQuery(
    ['helpers', helperKey],
    async () => {
      const response = await api.get(`/helpers/${helperKey}`);
      return response.data.data;
    },
    { retry: false, staleTime: Infinity }
  );

  // Dismiss mutation
  const dismissMutation = useMutation(
    async (dismissType: 'once' | 'permanent') => {
      await api.post(`/helpers/${helperKey}/dismiss`, { dismiss_type: dismissType });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['helpers', helperKey]);
        setIsOpen(false);
        if (showOnce) {
          toast.success('Hilfe geschlossen');
        }
      },
      onError: (error: any) => {
        toast.error('Fehler beim Schließen der Hilfe');
        console.error('Error dismissing helper:', error);
      }
    }
  );

  // Check if helper should be shown
  useEffect(() => {
    if (!isLoading && helperStatus) {
      // If permanently dismissed, never show
      if (helperStatus.dismissed && helperStatus.dismiss_type === 'permanent') {
        setIsOpen(false);
        return;
      }

      // If dismissed once and showOnce is true, don't show again
      if (helperStatus.dismissed && helperStatus.dismiss_type === 'once' && showOnce) {
        setIsOpen(false);
        return;
      }

      // Show helper if not dismissed or dismissed once but showOnce is false
      setIsOpen(true);
    }
  }, [helperStatus, isLoading, showOnce]);

  if (isLoading || !isOpen) {
    return null;
  }

  const handleDismiss = (dismissType: 'once' | 'permanent') => {
    dismissMutation.mutate(dismissType);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <HelpCircle className="h-6 w-6 text-blue-600" />
            <h3 className="text-xl font-bold text-gray-900">{title}</h3>
          </div>
          <button
            onClick={() => handleDismiss(showOnce ? 'once' : 'permanent')}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Schließen"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 py-6">
          <div className="prose max-w-none">
            {typeof content === 'string' ? (
              <div className="text-gray-700 whitespace-pre-wrap">{content}</div>
            ) : (
              content
            )}
          </div>
        </div>

        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex items-center justify-between">
          <div className="text-sm text-gray-500">
            Sie können diese Hilfe später wieder anzeigen lassen.
          </div>
          <div className="flex gap-3">
            {showPermanentOption && (
              <button
                onClick={() => handleDismiss('permanent')}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Nicht mehr anzeigen
              </button>
            )}
            <button
              onClick={() => handleDismiss('once')}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
            >
              {showOnce ? 'Verstanden' : 'Schließen'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

